import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Download, Palette, RefreshCw, Send, ImagePlus, Trash2, Camera, Film, Video } from "lucide-react";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import CarouselSlide from "@/components/CarouselSlide";
import { CAROUSEL_TEMPLATES, SLIDE_DIMENSIONS, getTemplate } from "@/lib/carouselTemplates";

/**
 * Slide builder — /tools/viral-carousel/slides/:vaultId
 *
 * Reads the carousel data from the Vault, applies the brand kit + active
 * template, renders every slide client-side at 1080×1350 and exports as a
 * ZIP of PNGs — zero AI cost, brand-consistent output.
 */
export default function CarouselSlides() {
  const { vaultId } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [vault, setVault] = useState(null);
  const [brand, setBrand] = useState(null);
  const [templateId, setTemplateId] = useState("minimal-white");

  const slideRefs = useRef({});

  // Phase 3 — photo carousel state.
  // `photos` is the pool of user-uploaded photos (each with focal analysis).
  // `slidePhotoIds[i]` maps slide index -> photo id (or null). When only one
  // photo is uploaded we auto-assign it to every slide.
  const [photos, setPhotos] = useState([]);
  const [slidePhotoIds, setSlidePhotoIds] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Phase 2 — reference-screenshot template extractor state.
  const [refFile, setRefFile] = useState(null);        // File instance (client-side)
  const [refExtracting, setRefExtracting] = useState(false);
  const [refModalOpen, setRefModalOpen] = useState(false);
  const [refLabel, setRefLabel] = useState("");

  const customTemplates = useMemo(() => (brand?.custom_templates || []), [brand]);
  const activeTemplate = getTemplate(templateId, customTemplates);
  const needsPhotos = !!activeTemplate.needsPhoto;
  const needsVideo = !!activeTemplate.needsVideo;

  // Reel render state — video upload + async encoding job.
  const [videoFile, setVideoFile] = useState(null);   // { id, url, filename, size }
  const [videoUploading, setVideoUploading] = useState(false);
  const [reelJob, setReelJob] = useState(null);       // { job_id, status, file_id?, error? }
  const [showSafeZones, setShowSafeZones] = useState(true);

  // Whenever the photo pool changes AND we've only uploaded one image, spray
  // it across every slide so the user sees output immediately. Explicit
  // per-slide overrides via the dropdown always win.
  useEffect(() => {
    if (photos.length === 1 && Object.keys(slidePhotoIds).length === 0) {
      // Read slide count off the loaded vault entry rather than the `slides`
      // memo (which is declared later in the render — TDZ).
      const o = vault?.output || {};
      const count = (o.cover_slide ? 1 : 0) + (o.slides?.length || 0) + (o.final_slide ? 1 : 0);
      if (count > 0) {
        const uniform = {};
        for (let i = 0; i < count; i++) uniform[i] = photos[0].id;
        setSlidePhotoIds(uniform);
      }
    }
  }, [photos.length, vault]);  // eslint-disable-line react-hooks/exhaustive-deps

  const getPhotoForSlide = (i) => {
    if (!photos.length) return null;
    const id = slidePhotoIds[i];
    if (id) return photos.find(p => p.id === id) || null;
    // Round-robin the pool for unassigned slides so >1 photo isn't stuck on
    // slide 0. Users can still explicitly assign via the dropdown.
    return photos[i % photos.length];
  };

  const uploadPhotos = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploadingPhotos(true);
    try {
      const form = new FormData();
      Array.from(fileList).slice(0, 10).forEach((f) => form.append("files", f));
      const { data } = await api.post("/carousel/photos", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      // Backend returns `/api/files/{id}` and `API` already includes `/api`,
      // so we strip the leading `/api` before joining to avoid doubling.
      const backend = API.replace(/\/api$/, "");
      const newOnes = (data.photos || []).map(p => ({ ...p, url: `${backend}${p.url}` }));
      setPhotos((prev) => [...prev, ...newOnes]);
      toast.success(`Uploaded ${newOnes.length} photo${newOnes.length === 1 ? "" : "s"} · analysed for smart placement`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingPhotos(false);
    }
  };

  const removePhoto = (photoId) => {
    setPhotos((prev) => prev.filter(p => p.id !== photoId));
    setSlidePhotoIds((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) if (next[k] === photoId) delete next[k];
      return next;
    });
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setVideoUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const backend = API.replace(/\/api$/, "");
      setVideoFile({ id: data.id, url: `${backend}${data.url}`, filename: file.name, size: file.size });
      setReelJob(null);
      toast.success("Video uploaded — ready to render");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed");
    } finally {
      setVideoUploading(false);
    }
  };

  // Poll a queued/encoding job until it lands. Runs while `reelJob` is
  // pending. Cleared by the reelJob state transition or component unmount.
  useEffect(() => {
    // Guard: skip polling when the job doesn't yet have a server-issued
    // job_id (happens for ~1s between clicking Render and the POST returning).
    if (!reelJob || !reelJob.job_id) return undefined;
    if (reelJob.status === "done" || reelJob.status === "failed") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/carousel/render-reel/${reelJob.job_id}`);
        if (cancelled) return;
        setReelJob(data);
        if (data.status === "done") toast.success("Your Reel is ready — click Download");
        if (data.status === "failed") toast.error(data.error || "Reel encoding failed");
      } catch {
        // Transient errors are fine — keep polling.
      }
    };
    const id = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [reelJob]);

  // Render every slide as a transparent-background text-only PNG. Used
  // as the FFmpeg overlay layer — the video is composited server-side.
  const renderOverlays = async () => {
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* ignore */ }
    }
    await new Promise((r) => requestAnimationFrame(r));
    const out = [];
    for (let i = 0; i < slides.length; i++) {
      const node = slideRefs.current[i];
      if (!node) continue;
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await toPng(node, {
        pixelRatio: 1,
        width: 1080, height: 1920,
        cacheBust: true, skipFonts: true,
        // Hide the video preview so the overlay is pure text-on-transparent.
        filter: (n) => {
          if (n.tagName === "VIDEO") return false;
          if (n.getAttribute && n.getAttribute("data-reel-guides") !== null) return false;
          return true;
        },
        style: {
          transform: "none", transformOrigin: "top left",
          width: "1080px", height: "1920px",
          background: "transparent",
        },
        backgroundColor: "transparent",
      });
      // eslint-disable-next-line no-await-in-loop
      const blob = await (await fetch(dataUrl)).blob();
      out.push({ filename: `overlay-${String(i + 1).padStart(2, "0")}.png`, blob });
    }
    return out;
  };

  const startReelRender = async () => {
    if (!videoFile) { toast.error("Upload a video first"); return; }
    try {
      setReelJob({ status: "rendering", job_id: null });
      const overlays = await renderOverlays();
      const form = new FormData();
      form.append("video_file_id", videoFile.id);
      overlays.forEach((o) => form.append("overlays", o.blob, o.filename));
      const { data } = await api.post("/carousel/render-reel", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setReelJob(data);   // triggers polling
      toast.info("Encoding your Reel — this usually takes 15-45 seconds");
    } catch (e) {
      setReelJob(null);
      toast.error(e?.response?.data?.detail || "Could not start reel render");
    }
  };

  // Loads Notion connection status so we can enable/disable "Send to Notion"
  // and route to Integrations if not connected. Failure is non-fatal.
  const [notion, setNotion] = useState({ connected: false, ready: false });
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/integrations/notion");
        if (!cancel) setNotion({
          connected: !!data.connected,
          ready: !!data.connected && !data.needs_database_pick && !!data.database_id,
          workspace: data.workspace_name,
        });
      } catch {
        if (!cancel) setNotion({ connected: false, ready: false });
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [v, bk] = await Promise.all([
          api.get(`/vault/${vaultId}`),
          api.get(`/brand-kit`),
        ]);
        if (cancel) return;
        setVault(v.data.item || v.data);
        setBrand(bk.data.brand_kit);
        setTemplateId(bk.data.brand_kit?.active_template_id || "minimal-white");
      } catch (e) {
        toast.error("Could not load this carousel");
        nav("/vault");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [vaultId, nav]);

  const slides = useMemo(() => {
    if (!vault?.output) return [];
    const o = vault.output;
    const list = [];
    if (o.cover_slide) list.push({ kind: "cover", data: o.cover_slide });
    (o.slides || []).forEach((s) => list.push({ kind: "body", data: s }));
    if (o.final_slide) list.push({ kind: "final", data: o.final_slide });
    return list;
  }, [vault]);

  // Render every slide DOM node to a PNG blob at full 1080×1350.
  //
  // Two subtleties to get right for Instagram-quality output:
  //   1. The preview uses `transform: scale(previewScale)` on the same node
  //      html-to-image captures — if we don't override it, the export paints
  //      the slide content at 32% inside a 1080×1350 canvas (tiny + huge
  //      whitespace). We pass `style: { transform: "none" }` to reset it on
  //      the cloned capture node.
  //   2. `pixelRatio: 2` renders the slide at 2160×2700, which Instagram
  //      downsamples to sharp 1080×1350 (Retina-crisp text and edges).
  //      Anything less than 2× produces the "shotty" softness the user
  //      reported.
  //
  // Reused by both ZIP export and Notion push. Awaits fonts so text renders
  // deterministically instead of falling back to system defaults.
  const renderAllSlidesToBlobs = async () => {
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* fonts already ready */ }
    }
    await new Promise((r) => requestAnimationFrame(r));
    const out = [];
    for (let i = 0; i < slides.length; i++) {
      const node = slideRefs.current[i];
      if (!node) continue;
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        width: SLIDE_DIMENSIONS.width,
        height: SLIDE_DIMENSIONS.height,
        cacheBust: true,
        // Skip the (blocked) remote-CSS scrape for Google Fonts stylesheets —
        // the fonts are already loaded in the live DOM so the capture uses
        // them directly. Leaving this enabled produces noisy CORS warnings
        // in the console without changing output.
        skipFonts: true,
        // Override the preview transform so the capture paints at 1:1.
        style: {
          transform: "none",
          transformOrigin: "top left",
          width: `${SLIDE_DIMENSIONS.width}px`,
          height: `${SLIDE_DIMENSIONS.height}px`,
        },
      });
      // eslint-disable-next-line no-await-in-loop
      const blob = await (await fetch(dataUrl)).blob();
      out.push({ filename: `slide-${String(i + 1).padStart(2, "0")}.png`, dataUrl, blob });
    }
    return out;
  };

  const exportAll = async () => {
    if (!slides.length) return;
    // The Reel template renders at 1080×1920 for server-side FFmpeg burn-in.
    // A ZIP of still PNGs isn't what a user wants from a Reel — steer them
    // to the "Render Reel" flow instead of exporting cropped/misaligned frames.
    if (needsVideo) {
      toast.error("Use \"Render Reel with text burned in\" — PNG export is for static carousels only.");
      return;
    }
    setBusy(true);
    try {
      const rendered = await renderAllSlidesToBlobs();
      const zip = new JSZip();
      rendered.forEach((r) => {
        const b64 = r.dataUrl.split(",")[1];
        zip.file(r.filename, b64, { base64: true });
      });
      // Attach caption as a plain text file for convenience.
      if (vault.output?.caption) zip.file("caption.txt", vault.output.caption);
      if (vault.output?.pinned_comment) zip.file("pinned-comment.txt", vault.output.pinned_comment);

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `carousel-${vaultId.slice(-8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rendered.length} slides`);
    } catch (e) {
      toast.error("Export failed. If it keeps happening, try a different template.");
    } finally {
      setBusy(false);
    }
  };

  const [sending, setSending] = useState(false);
  const sendToNotion = async () => {
    if (needsVideo) {
      toast.error("Reel MP4s can't push to Notion via still images — use the Render Reel button, then upload the MP4 to Notion manually.");
      return;
    }
    if (!notion.ready) {
      toast.error("Connect Notion and pick a target database first.");
      nav("/integrations");
      return;
    }
    setSending(true);
    try {
      const rendered = await renderAllSlidesToBlobs();
      const form = new FormData();
      form.append("title", vault.title || "DMS Carousel");
      if (vault.output?.caption) form.append("caption", vault.output.caption);
      if (vault.output?.pinned_comment) form.append("pinned_comment", vault.output.pinned_comment);
      rendered.forEach((r) => form.append("slides", r.blob, r.filename));

      const { data } = await api.post("/notion/push-carousel", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,   // Notion uploads can take up to a minute on large carousels
      });
      toast.success(`Sent ${data.slide_count} slides to Notion`);
      if (data.page_url) {
        // Give the toast a moment before opening — feels less abrupt.
        setTimeout(() => window.open(data.page_url, "_blank", "noopener"), 400);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Send to Notion failed";
      toast.error(String(detail).slice(0, 200));
    } finally {
      setSending(false);
    }
  };

  const saveTemplateAsDefault = async () => {
    try {
      await api.put("/brand-kit", { active_template_id: templateId });
      toast.success("Set as default for future carousels");
    } catch { toast.error("Could not save default"); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1D4E89]" />
      </div>
    );
  }

  if (!slides.length) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="dms-card p-8 text-center">
          <p className="text-[15px] text-[#0A2342]">This vault item doesn&rsquo;t look like a carousel — we can&rsquo;t build slides from it.</p>
          <Link to="/vault" className="dms-btn-primary mt-4 inline-flex">Back to vault</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-10" data-testid="carousel-slides-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          <Link to={`/vault`} className="dms-link inline-flex items-center gap-2 text-[13px]">
            <ArrowLeft className="w-4 h-4" /> Back to vault
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mt-2 text-[#0A2342]">Slide builder</h1>
          <p className="text-[13.5px] text-[#64748B] mt-1">
            {slides.length} slides · uses your brand kit ·&nbsp;
            <Link to="/settings/brand-kit" className="dms-link">edit brand</Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="save-template-default"
            onClick={saveTemplateAsDefault}
            className="dms-btn-ghost"
          >
            <RefreshCw className="w-4 h-4" /> Save template as default
          </button>
          <button
            data-testid="send-to-notion"
            onClick={sendToNotion}
            disabled={sending || busy}
            title={notion.ready ? `Send to ${notion.workspace || "Notion"}` : "Connect Notion in Integrations first"}
            className={`inline-flex items-center gap-2 text-[13.5px] font-semibold py-2.5 px-4 rounded-md transition ${
              notion.ready
                ? "bg-white border border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                : "bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0] cursor-not-allowed"
            }`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send to Notion"}
          </button>
          <button
            data-testid="export-slides"
            onClick={exportAll}
            disabled={busy || sending}
            className="dms-btn-primary"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy ? "Exporting…" : `Export ${slides.length} PNGs`}
          </button>
        </div>
      </div>

      {/* Template picker */}
      <div className="dms-card p-4 sm:p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="dms-label inline-flex items-center gap-2"><Palette className="w-3.5 h-3.5" /> Template</div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="open-reference-modal"
              onClick={() => setRefModalOpen(true)}
              className="text-[12px] dms-link inline-flex items-center gap-1"
              title="Upload a design screenshot to auto-extract a custom template"
            >
              <ImagePlus className="w-3.5 h-3.5" />
              Upload reference → new template
            </button>
            <Link to="/settings/brand-kit" className="text-[12px] dms-link">Change brand colours</Link>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {CAROUSEL_TEMPLATES.map((t) => (
            <button
              key={t.id}
              data-testid={`pick-template-${t.id}`}
              onClick={() => setTemplateId(t.id)}
              className={`text-left border rounded-lg overflow-hidden transition ${templateId === t.id ? "border-[#1D4E89] ring-2 ring-[#1D4E89]/20" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}
            >
              <div style={{ background: t.thumbnail.bg, height: 64 }} className="flex items-center justify-center">
                <span style={{ color: t.thumbnail.accent, fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                  {t.name}
                </span>
              </div>
              <div className="p-2 text-[11.5px] font-medium text-[#0A2342] truncate">{t.name}</div>
            </button>
          ))}
          {customTemplates.map((ct) => {
            const bg = ct.background === "gradient_diagonal"
              ? `linear-gradient(135deg, ${ct.colours?.primary || "#0A2342"}, ${ct.colours?.secondary || "#1D4E89"})`
              : ct.background === "off_white_paper" ? "#F8F6F1" : (ct.colours?.primary || "#0A2342");
            return (
              <div key={ct.id} className="relative group">
                <button
                  data-testid={`pick-template-${ct.id}`}
                  onClick={() => setTemplateId(ct.id)}
                  className={`w-full text-left border rounded-lg overflow-hidden transition ${templateId === ct.id ? "border-[#1D4E89] ring-2 ring-[#1D4E89]/20" : "border-[#E2E8F0] hover:border-[#94A3B8]"}`}
                >
                  <div style={{ background: bg, height: 64 }} className="flex items-center justify-center">
                    <span style={{ color: ct.colours?.accent || "#F59E0B", fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                      {(ct.label || "Custom").slice(0, 18)}
                    </span>
                  </div>
                  <div className="p-2 text-[11.5px] font-medium text-[#0A2342] truncate flex items-center gap-1">
                    <ImagePlus className="w-3 h-3 text-[#4FD1C5] shrink-0" />
                    <span className="truncate">{ct.label || "Custom template"}</span>
                  </div>
                </button>
                <button
                  data-testid={`delete-template-${ct.id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Delete "${ct.label || 'this custom template'}"?`)) return;
                    try {
                      await api.delete(`/carousel/custom-templates/${ct.id}`);
                      setBrand((b) => ({ ...b, custom_templates: (b?.custom_templates || []).filter(x => x.id !== ct.id) }));
                      toast.success("Template deleted");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  className="absolute top-1 right-1 bg-white/95 rounded p-0.5 opacity-0 group-hover:opacity-100 transition"
                  aria-label="Delete custom template"
                >
                  <Trash2 className="w-3 h-3 text-[#B91C1C]" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reference-screenshot upload modal */}
      {refModalOpen && (
        <div
          data-testid="reference-modal"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={() => !refExtracting && setRefModalOpen(false)}
        >
          <div className="dms-card p-6 max-w-lg w-full bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="dms-label mb-2 inline-flex items-center gap-2"><ImagePlus className="w-3.5 h-3.5" /> Extract template from a screenshot</div>
            <h2 className="font-heading font-bold text-xl text-[#0A2342] leading-tight">Upload a design reference</h2>
            <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed">
              Drop in a slide, ad or Pinterest inspiration. DMS reads its colour palette, layout and accent style, then saves it as a private custom template you can pick alongside the presets. It&apos;s a style match, not a pixel copy.
            </p>
            <label htmlFor="reference-file" className="mt-4 block cursor-pointer rounded-xl border-2 border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-6 text-center hover:border-[#4FD1C5] transition">
              <ImagePlus className="w-6 h-6 mx-auto text-[#1D4E89] mb-2" />
              <div className="font-semibold text-[#0A2342] text-[13.5px]">{refFile ? refFile.name : "Choose a PNG / JPG / WEBP (max 10 MB)"}</div>
              <input
                id="reference-file"
                data-testid="reference-file-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => setRefFile(e.target.files?.[0] || null)}
              />
            </label>
            <div className="mt-4">
              <label className="dms-label mb-1 block">Name it (optional)</label>
              <input
                data-testid="reference-label-input"
                value={refLabel}
                onChange={(e) => setRefLabel(e.target.value.slice(0, 40))}
                placeholder="e.g. Editorial navy & coral"
                className="dms-input w-full text-[14px]"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                data-testid="reference-cancel"
                onClick={() => { setRefModalOpen(false); setRefFile(null); setRefLabel(""); }}
                disabled={refExtracting}
                className="dms-btn-ghost"
              >Cancel</button>
              <button
                data-testid="reference-extract"
                disabled={!refFile || refExtracting}
                onClick={async () => {
                  if (!refFile) return;
                  setRefExtracting(true);
                  try {
                    const fd = new FormData();
                    fd.append("file", refFile);
                    if (refLabel.trim()) fd.append("label", refLabel.trim());
                    const { data } = await api.post("/carousel/extract-template", fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    // Merge the new template into brand.custom_templates and activate it.
                    setBrand((b) => ({
                      ...b,
                      custom_templates: [...(b?.custom_templates || []), data.template],
                    }));
                    setTemplateId(data.template.id);
                    setRefModalOpen(false);
                    setRefFile(null);
                    setRefLabel("");
                    toast.success(`Custom template saved (${data.template.confidence} confidence).`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Extraction failed");
                  } finally {
                    setRefExtracting(false);
                  }
                }}
                className="dms-btn-primary"
              >
                {refExtracting ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : "Extract & save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo panel — shown only for templates that use a background image */}
      {needsPhotos && (
        <div className="dms-card p-4 sm:p-5 mb-6" data-testid="photo-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="dms-label inline-flex items-center gap-2"><Camera className="w-3.5 h-3.5" /> Slide photos</div>
            <div className="text-[11.5px] text-[#64748B]">
              {photos.length === 0
                ? "Upload up to 10 photos — DMS will detect the subject and place text where it won't cover anything important."
                : photos.length === 1
                  ? "1 photo — used across every slide."
                  : `${photos.length} photos — assign one per slide, or leave blank to auto-rotate.`}
            </div>
          </div>

          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {photos.map((p) => (
                <div key={p.id} className="group relative w-24 h-24 rounded-lg overflow-hidden border border-[#E2E8F0]" data-testid={`photo-thumb-${p.id}`}>
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                  <button
                    data-testid={`photo-remove-${p.id}`}
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-1 right-1 bg-white/95 rounded p-0.5 opacity-0 group-hover:opacity-100 transition"
                    aria-label="Remove photo"
                  >
                    <Trash2 className="w-3 h-3 text-[#B91C1C]" />
                  </button>
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate" title={p.focal?.description || ""}>
                    {p.focal?.subject_zone || "center"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <label
            data-testid="photo-upload-btn"
            className={`inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#1D4E89] hover:bg-[#F0F7FF] transition ${uploadingPhotos ? "opacity-60 pointer-events-none" : ""}`}
          >
            {uploadingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            <span className="text-[13.5px] font-medium text-[#0A2342]">
              {uploadingPhotos ? "Analysing photos…" : (photos.length ? "Add more photos" : "Upload photos")}
            </span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }}
            />
          </label>
        </div>
      )}

      {/* Video panel — only for the Reel · Full Bleed template */}
      {needsVideo && (
        <div className="dms-card p-4 sm:p-5 mb-6" data-testid="reel-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="dms-label inline-flex items-center gap-2"><Film className="w-3.5 h-3.5" /> Reel video</div>
              <div className="text-[12px] text-[#64748B] mt-1">
                Upload one MP4 (max 60&nbsp;s / 100&nbsp;MB). DMS will trim, scale to 1080×1920 and burn your slide captions into the safe zone away from Instagram&rsquo;s UI.
              </div>
            </div>
            <label className="flex items-center gap-2 text-[12.5px] text-[#0A2342] cursor-pointer">
              <input
                type="checkbox" checked={showSafeZones}
                onChange={(e) => setShowSafeZones(e.target.checked)}
                data-testid="toggle-safe-zones"
              />
              Show IG safe-zone guides
            </label>
          </div>

          {videoFile ? (
            <div className="flex items-center gap-4 p-3 bg-[#F8FAFC] rounded-lg">
              <Video className="w-6 h-6 text-[#1D4E89]" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-medium text-[#0A2342] truncate">{videoFile.filename}</div>
                <div className="text-[11.5px] text-[#64748B]">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB · ready to render</div>
              </div>
              <button
                data-testid="reel-video-remove"
                onClick={() => { setVideoFile(null); setReelJob(null); }}
                className="text-[#B91C1C] hover:bg-[#FEF2F2] p-2 rounded"
                aria-label="Remove video"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label
              data-testid="reel-video-upload"
              className={`inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#1D4E89] hover:bg-[#F0F7FF] transition ${videoUploading ? "opacity-60 pointer-events-none" : ""}`}
            >
              {videoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              <span className="text-[13.5px] font-medium text-[#0A2342]">
                {videoUploading ? "Uploading…" : "Upload MP4"}
              </span>
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={(e) => { uploadVideo(e.target.files?.[0]); e.target.value = ""; }}
              />
            </label>
          )}

          {/* Render + download row */}
          {videoFile && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                data-testid="render-reel-btn"
                onClick={startReelRender}
                disabled={reelJob && ["queued", "encoding", "rendering"].includes(reelJob.status)}
                className="dms-btn-primary"
              >
                {(reelJob && ["queued", "encoding", "rendering"].includes(reelJob.status))
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Encoding your reel…</>
                  : <><Film className="w-4 h-4" /> Render Reel with text burned in</>}
              </button>

              {reelJob?.status === "done" && (
                <a
                  data-testid="reel-download-btn"
                  href={`${API}/files/${reelJob.file_id}`}
                  download
                  className="dms-btn-accent"
                >
                  <Download className="w-4 h-4" /> Download reel MP4
                </a>
              )}
              {reelJob?.status === "failed" && (
                <span className="text-[12.5px] text-[#B91C1C]">{reelJob.error || "Encoding failed"}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Slide grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {slides.map((s, i) => (
          <div key={i} className="flex flex-col items-center gap-2" data-testid={`slide-${i}`}>
            <CarouselSlide
              ref={(el) => { slideRefs.current[i] = el; }}
              slide={s.data}
              brand={brand}
              templateId={templateId}
              kind={s.kind}
              meta={{
                index: i + 1, total: slides.length,
                photo: needsPhotos ? getPhotoForSlide(i) : null,
                video: needsVideo ? videoFile : null,
              }}
              previewScale={needsVideo ? 0.22 : 0.32}
              showSafeZones={needsVideo && showSafeZones}
            />
            {needsPhotos && photos.length > 1 && (
              <select
                data-testid={`slide-photo-picker-${i}`}
                value={slidePhotoIds[i] || ""}
                onChange={(e) => setSlidePhotoIds((prev) => ({ ...prev, [i]: e.target.value || null }))}
                className="text-[11.5px] border border-[#E2E8F0] rounded-md px-2 py-1 bg-white text-[#0A2342] max-w-[180px]"
              >
                <option value="">Auto (rotate)</option>
                {photos.map((p, idx) => (
                  <option key={p.id} value={p.id}>Photo {idx + 1}</option>
                ))}
              </select>
            )}
            <div className="text-[11.5px] text-[#64748B] font-medium">
              Slide {i + 1} · {s.kind === "cover" ? "Cover" : s.kind === "final" ? "CTA" : "Body"}
            </div>
          </div>
        ))}
      </div>

      {/* Caption preview */}
      {vault.output?.caption && (
        <div className="mt-10 dms-card p-5 sm:p-6">
          <div className="dms-label">Caption (copy this into Instagram)</div>
          <pre className="mt-3 whitespace-pre-wrap text-[14.5px] text-[#0A2342] font-sans leading-relaxed">
            {vault.output.caption}
          </pre>
        </div>
      )}
    </div>
  );
}
